import type { ProjectPermission } from '../types';
import type { ProjectDocument, ProjectSchema } from '@readme/backend/models/project/types';
import type { PartialDeep } from 'type-fest';

import { getProjectPermission } from '@readme/plans';

import { typesToVerbage, groupTypesToVerbage } from '../types';
import { daysRemaining } from '../utils/date';

/**
 * Determines if a project's grace period is currently active. Returns `false`
 * to indicate that the grace period has expired.
 */
function isGracePeriodActive(project: PartialDeep<ProjectSchema>) {
  return !!project.gracePeriod?.enabled && daysRemaining(project.gracePeriod?.endsAt) > 0;
}

/**
 * Determines if a project's trial period is currently active. Returns `false`
 * to indicate that the trial period has expired.
 */
function isTrialPeriodActive(project: PartialDeep<ProjectSchema>) {
  return !!project.trial?.trialDeadlineEnabled && daysRemaining(project.trial?.trialEndsAt) > 0;
}

/**
 * Determines if the provided `project` is in an active trial state or not,
 * which includes more checks than simply evaluating trial periods. Use this as
 * the source of truth whether a project should have the "trial" experience
 * enabled or not.
 */
export function isTrialEnabled(project: PartialDeep<ProjectSchema>): boolean {
  const { is_active: isActive = false, flags: { superHub } = {}, plan = '', planOverride = '' } = project;

  /** True if project is on a free-ish plan. */
  const isFreePlan = ['free'].includes(planOverride || plan);

  /**
   * For superhub projects, nix the requirement for trial periods. Otherwise,
   * continue to verify we are within either the trial or grace period window.
   */
  const inTrialWindow = superHub ? true : isTrialPeriodActive(project) || isGracePeriodActive(project);

  // To enable trial mode, the following must ALL be true:
  // - Project plan must be free
  // - Project must not be activated
  // - Project must be superhub or within trial or grace period window
  return isFreePlan && !isActive && inTrialWindow;
}

/**
 * Determines if a project's trial period has expired. Returns `true`
 * to indicate that the trial period has expired.
 */
export function isTrialPeriodExpired(project: PartialDeep<ProjectSchema>): boolean {
  // For a trial to be expired, all must be true:
  // - Project must be in a trial mode
  // - Project's trial period is no longer active
  // - Project's grace period is no longer active
  return isTrialEnabled(project) && !isTrialPeriodActive(project) && !isGracePeriodActive(project);
}

export function isCustomDomainAllowed(project: ProjectSchema): boolean {
  // If project is trial and the grace period is not expired allow them to use custom domains. This is will be the state when projects transfer owners.
  const isTrial = project.plan === 'free';
  if (isTrial && isGracePeriodActive(project)) return true;

  return getProjectPermission(project, 'custom_domain');
}

export function getProjectBaseUrl(
  env: string,
  siteDomain: string,
  project: ProjectDocument,
  staging = false,
  port: number | string = 3000,
): string {
  const isStagingEnabled = (p: ProjectDocument) => {
    const topLevelProject = p.childrenProjects?.length ? p : p._parent;
    if (!topLevelProject) return false;
    return (topLevelProject as ProjectDocument).flags.staging;
  };

  if (env === 'development' && (!isStagingEnabled(project) || staging)) {
    return `http://${project.subdomain}.readme.local:${port}/`;
  }

  const renderNotProd = new Set(['pr', 'next', 'stage', 'preview']);

  const customDomain = (project._parent as ProjectDocument)
    ? (project._parent as ProjectDocument).custom_domain
    : project.custom_domain;
  // go to custom domain, but NOT in preview
  if (renderNotProd.has(env) && env !== 'preview' && isStagingEnabled(project) && !staging) {
    return `https://${customDomain}/`;
  }

  if (env === 'development' && !staging) {
    return `http://${customDomain}:${port}/`;
  }

  if (customDomain && isCustomDomainAllowed(project) && !staging && !renderNotProd.has(env)) {
    return `https://${customDomain}/`;
  }

  return `https://${project.subdomain}.${siteDomain}/`;
}

export function getProjectHomeUrl(
  env: string,
  siteDomain: string,
  project: ProjectDocument,
  port: number | string = 3000,
  ...paths: (boolean | string)[]
): string {
  let baseUrl = getProjectBaseUrl(env, siteDomain, project, false, port);

  if (paths.length === 1 && paths[0] === false) {
    paths = [];
  }

  // Child projects should have themselves
  // (either their subpath or their subdomain) in their home URL.
  if (project._parent && paths[0] !== project.subpath && paths[0] !== project.subdomain) {
    baseUrl = getProjectBaseUrl(env, siteDomain, project._parent as ProjectDocument, false, port);
    paths.unshift(project.subpath || project.subdomain);
  }

  return baseUrl + paths.join('/').replace(/^\//, '');
}

/** return the userType in either singular or plural */
export function fromUserType(userType: ProjectPermission['userType'], singular: boolean): string {
  const toVerbage = singular ? typesToVerbage : groupTypesToVerbage;
  return toVerbage[userType];
}

export * from './appearance';
export * from './permissions';
